import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import Flex from '~/shared/components/Flex';
import { useTranslation } from '~/shared/utils/translation';
import { useCheckoutUtils } from '../lib/useCheckoutUtils';
import { useCheckout } from '~/features/checkout/hooks';
import {
    CardDeliveryAddressEdit,
    CardDeliveryAddressInfo,
    CardDeliveryAddressTitle,
    CardDeliveryDays,
    CardFreightPrice,
    CardInfoWrapper,
    CardRadioWrapper,
    CardTitle,
    CardWrapper,
} from './styled';
import { Form, Radio } from '~/shared/components/form';
import { DeliveryMethod, DeliveryTypes } from '~/features/checkout/models/checkout';
import type { Store } from '~/lib/data-contract/salesforce';
import { useScrollLock } from '~/shared/hooks';
import { Button, Image } from '~/shared/components';
import { Drawer } from '~/shared/components/Drawer';
import { useBasket } from '~/features/basket';
import { DeliveryStepResolver } from '../../DeliveryStepResolver';
import { StyledMessage } from '../components/DeliveryContent/styled';
import { SuggestedStoreChangeDialog } from '../components/SuggestedStoreChangeDialog';
import { theme } from '~/theme';

type DeliveryPointChangeRequiringConfirmation =
    | { type: 'deliveryMethod'; method: DeliveryTypes }
    | { type: 'clickAndCollectStore'; store: Store }
    | null;

export const ShippingMethods = () => {
    const { translate } = useTranslation();
    const { getDays, getMinPrice } = useCheckoutUtils();
    const {
        carrier,
        carriers,
        currentShipmentStep,
        deliveryAddress,
        vendorSelectedDroppoint,
        setCarrier,
        setDeliveryMethod,
        setDeliveryAddress,
    } = useCheckout();

    const { lock, unlock } = useScrollLock();
    const basket = useBasket();
    const methods = useForm();
    const [chosenShippingMethod, setChosenShippingMethod] = useState<DeliveryTypes>(
        DeliveryTypes.clickAndCollect,
    );
    const [showStepDrawer, setShowStepDrawer] = useState(false);
    const [deliveryPointChangeRequiringConfirmation, setDeliveryPointChangeRequiringConfirmation] =
        useState<DeliveryPointChangeRequiringConfirmation>(null);
    const appliedSuggestedStoreId = useRef<string | null>(null);
    const suggestedStore = basket?.c_suggestedStore ?? null;
    const suggestedStoreId = suggestedStore?.storeId ?? null;

    const deliveryAddressStoreId =
        deliveryAddress && 'storeId' in deliveryAddress ? deliveryAddress.storeId ?? null : null;
    const handleCloseDrawer = () => {
        setShowStepDrawer(false);
    };

    const handleResetDeliveryInformation = () => {
        setCarrier(undefined);
        setDeliveryAddress(null);
    };
    const defaultClickAndCollectOption = carriers.clickAndCollect[0];

    useEffect(() => {
        if (vendorSelectedDroppoint) {
            handleCheckboxChange(DeliveryTypes.dropPoint, true);
            setDeliveryMethod('dropPoint');
            setCarrier(carriers.dropPoint[0]);
        } else {
            // we do not want to reset carrier information when vendorSelectedDroppoint is true
            setChosenShippingMethod(DeliveryTypes.clickAndCollect);
            setDeliveryMethod('clickAndCollect');
            setCarrier(defaultClickAndCollectOption);
            if (!suggestedStoreId) {
                setDeliveryAddress(null);
            }
        }
    }, [currentShipmentStep, vendorSelectedDroppoint]);

    type DeliveryMethodMap = {
        [key in DeliveryTypes]?: DeliveryMethod;
    };
    const deliveryMethodMap: DeliveryMethodMap = {
        [DeliveryTypes.clickAndCollect]: 'clickAndCollect',
        [DeliveryTypes.dropPoint]: 'dropPoint',
        [DeliveryTypes.addressDelivery]: 'addressDelivery',
    };

    useEffect(() => {
        if (showStepDrawer) {
            lock();
        }
        return () => {
            unlock();
        };
    }, [lock, unlock, showStepDrawer]);

    // a new suggested store takes over the delivery point, even when one is already chosen
    useEffect(() => {
        if (appliedSuggestedStoreId.current === suggestedStoreId) {
            return;
        }

        if (suggestedStore && !defaultClickAndCollectOption) {
            // carriers are not loaded yet, we apply the suggestion once they are
            return;
        }

        appliedSuggestedStoreId.current = suggestedStoreId;

        if (!suggestedStore) {
            return;
        }

        setChosenShippingMethod(DeliveryTypes.clickAndCollect);
        setDeliveryMethod('clickAndCollect');
        setCarrier(defaultClickAndCollectOption);
        setDeliveryAddress({
            _type: 'store',
            id: suggestedStore.storeId,
            name: suggestedStore.storeName,
            address1: suggestedStore.address1,
            address2: suggestedStore.address2 ?? '',
            city: suggestedStore.city,
            countryCode: suggestedStore.countryCode,
            postalCode: suggestedStore.postalCode,
        });
    }, [defaultClickAndCollectOption, suggestedStore, suggestedStoreId]);

    const deliveryType = deliveryAddress?.deliveryType;

    const requestDeliveryPointChangeConfirmation = (
        change: Exclude<DeliveryPointChangeRequiringConfirmation, null>,
    ) => {
        // we only show the dialog while the suggested store is still the chosen delivery point
        if (suggestedStore?.storeId && deliveryAddressStoreId === suggestedStore.storeId) {
            setDeliveryPointChangeRequiringConfirmation(change);
            return true;
        }

        return false;
    };

    const selectClickAndCollectStore = (store: Store) => {
        setDeliveryAddress({
            _type: 'store',
            id: store.id,
            name: store.name,
            address1: store.address1,
            address2: store.address2 ?? '',
            city: store.city,
            countryCode: store.countryCode,
            postalCode: store.postalCode ?? store.postal_code ?? '',
        });
        handleCloseDrawer();
    };

    const handleClickAndCollectStoreSelectRequest = (store: Store) => {
        if (store.id === suggestedStore?.storeId) return false;

        return requestDeliveryPointChangeConfirmation({
            type: 'clickAndCollectStore',
            store,
        });
    };

    const handleCancelDeliveryPointChange = () => {
        setDeliveryPointChangeRequiringConfirmation(null);
        handleCloseDrawer();
    };

    const handleConfirmDeliveryPointChange = async () => {
        const change = deliveryPointChangeRequiringConfirmation;
        if (!change) return;

        setDeliveryPointChangeRequiringConfirmation(null);

        try {
            await basket.setSuggestedStore(null);
        } catch (error) {
            console.warn('Could not clear suggested store', error);
        }

        if (change.type === 'deliveryMethod') {
            handleCheckboxChange(change.method, true);
            return;
        }

        selectClickAndCollectStore(change.store);
    };

    const handleCheckboxChange = (
        shippingMethod: DeliveryTypes,
        skipDeliveryPointConfirmation = false,
    ) => {
        if (
            !skipDeliveryPointConfirmation &&
            shippingMethod !== DeliveryTypes.clickAndCollect &&
            requestDeliveryPointChangeConfirmation({
                type: 'deliveryMethod',
                method: shippingMethod,
            })
        ) {
            return;
        }

        setChosenShippingMethod(shippingMethod);
        const method = deliveryMethodMap[shippingMethod];

        if (method) {
            setDeliveryMethod(method);
        }

        handleResetDeliveryInformation();

        if (!vendorSelectedDroppoint) {
            setShowStepDrawer(true);
        }
    };

    const isDeliveryAddressEmpty = useMemo(() => {
        if (deliveryAddress) {
            if (Object.keys(deliveryAddress).length === 0) {
                return true;
            }
            return false;
        } else {
            return true;
        }
    }, [deliveryAddress]);

    return (
        <>
            <div>
                <SuggestedStoreChangeDialog
                    isOpen={deliveryPointChangeRequiringConfirmation !== null}
                    storeName={suggestedStore?.storeName ?? ''}
                    onConfirm={handleConfirmDeliveryPointChange}
                    onCancel={handleCancelDeliveryPointChange}
                />
                <Form methods={methods}>
                    <CardWrapper
                        data-testid="click-and-collect"
                        onClick={() => {
                            if (chosenShippingMethod !== DeliveryTypes.clickAndCollect) {
                                handleCheckboxChange(DeliveryTypes.clickAndCollect);
                            }
                        }}
                        disabled={vendorSelectedDroppoint}
                    >
                        <Flex justifyContent="space-between">
                            <CardInfoWrapper>
                                <CardRadioWrapper>
                                    <Radio
                                        checked={
                                            chosenShippingMethod === DeliveryTypes.clickAndCollect
                                        }
                                        aria-label={`Levering, ${translate('checkout.checkout.delivery.option.butik')}`}
                                        name="butik"
                                        onKeyDown={(e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                if (
                                                    chosenShippingMethod !==
                                                    DeliveryTypes.clickAndCollect
                                                ) {
                                                    handleCheckboxChange(
                                                        DeliveryTypes.clickAndCollect,
                                                    );
                                                }
                                            }
                                        }}
                                    />
                                </CardRadioWrapper>

                                <CardTitle>
                                    {translate('checkout.checkout.delivery.option.butik')}
                                </CardTitle>

                                <CardDeliveryDays>
                                    {getDays(carriers?.clickAndCollect)}
                                </CardDeliveryDays>

                                {chosenShippingMethod === DeliveryTypes.clickAndCollect &&
                                    isDeliveryAddressEmpty && (
                                        <Button
                                            data-testid="select-store"
                                            style={{
                                                marginTop: theme.space['4'],
                                                fontSize: theme.fontSizes['xs'],
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowStepDrawer(true);
                                            }}
                                        >
                                            {translate('checkout.checkout.delivery.location')}
                                        </Button>
                                    )}

                                {deliveryType === 'store' && deliveryAddress && (
                                    <CardDeliveryAddressInfo>
                                        <Flex justifyContent="space-between">
                                            <CardDeliveryAddressTitle>
                                                {deliveryAddress?.firstName}
                                            </CardDeliveryAddressTitle>
                                            <CardDeliveryAddressEdit
                                                onClick={() => setShowStepDrawer(true)}
                                                tabIndex={0}
                                                onKeyDown={(e: React.KeyboardEvent) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        setShowStepDrawer(true);
                                                    }
                                                }}
                                            >
                                                {translate('checkout.delivery.edit')}
                                            </CardDeliveryAddressEdit>
                                        </Flex>
                                        <div style={{ paddingBottom: '6px' }}>
                                            {deliveryAddress?.address1}
                                        </div>
                                        <div>
                                            {`${deliveryAddress?.postalCode} ${deliveryAddress?.city}`}{' '}
                                        </div>
                                    </CardDeliveryAddressInfo>
                                )}
                            </CardInfoWrapper>

                            <CardFreightPrice>
                                {getMinPrice(carriers?.clickAndCollect)}
                            </CardFreightPrice>
                        </Flex>
                    </CardWrapper>

                    {!!carriers.dropPoint.length && (
                        <CardWrapper
                            data-testid="carrierbox"
                            onClick={() => {
                                if (
                                    chosenShippingMethod !== DeliveryTypes.dropPoint &&
                                    !vendorSelectedDroppoint
                                ) {
                                    handleCheckboxChange(DeliveryTypes.dropPoint);
                                }
                            }}
                        >
                            <Flex justifyContent="space-between">
                                <CardInfoWrapper>
                                    <CardRadioWrapper>
                                        <Radio
                                            checked={
                                                chosenShippingMethod === DeliveryTypes.dropPoint
                                            }
                                            aria-label={`Levering, ${translate('checkout.checkout.delivery.option.shop')}`}
                                            name="shop"
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    if (
                                                        chosenShippingMethod !==
                                                            DeliveryTypes.dropPoint &&
                                                        !vendorSelectedDroppoint
                                                    ) {
                                                        handleCheckboxChange(
                                                            DeliveryTypes.dropPoint,
                                                        );
                                                    }
                                                }
                                            }}
                                        />
                                    </CardRadioWrapper>
                                    <CardTitle>
                                        {translate('checkout.checkout.delivery.option.shop')}
                                    </CardTitle>
                                    <CardDeliveryDays>
                                        {getDays(carriers?.dropPoint)}
                                    </CardDeliveryDays>

                                    {chosenShippingMethod === DeliveryTypes.dropPoint &&
                                        isDeliveryAddressEmpty && (
                                            <Button
                                                style={{
                                                    marginTop: theme.space['4'],
                                                    fontSize: theme.fontSizes['xs'],
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setShowStepDrawer(true);
                                                }}
                                            >
                                                {translate('checkout.checkout.delivery.shop')}
                                            </Button>
                                        )}

                                    <CardDeliveryAddressInfo
                                        isHidden={deliveryType !== 'droppoint'}
                                    >
                                        <Flex justifyContent="space-between">
                                            <div style={{ paddingBottom: '5px' }}>
                                                {carrier?.dropPoint?.c_image && (
                                                    <Image
                                                        src={carrier?.dropPoint?.c_image}
                                                        alt={carrier?.dropPoint?.name ?? ''}
                                                        height={24}
                                                        width={35}
                                                        layout="intrinsic"
                                                        objectFit="contain"
                                                    />
                                                )}

                                                <CardTitle>{carrier?.dropPoint?.name}</CardTitle>
                                            </div>

                                            <CardDeliveryAddressEdit
                                                onClick={() => setShowStepDrawer(true)}
                                                tabIndex={0}
                                                onKeyDown={(e: React.KeyboardEvent) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        setShowStepDrawer(true);
                                                    }
                                                }}
                                            >
                                                {translate('checkout.delivery.edit')}
                                            </CardDeliveryAddressEdit>
                                        </Flex>
                                        <div style={{ paddingBottom: '6px' }}>
                                            <CardDeliveryAddressTitle>
                                                {deliveryAddress?.firstName}
                                            </CardDeliveryAddressTitle>

                                            {deliveryAddress?.address1}
                                        </div>
                                        <div>
                                            {`${deliveryAddress?.postalCode} ${deliveryAddress?.city}`}{' '}
                                        </div>
                                    </CardDeliveryAddressInfo>
                                </CardInfoWrapper>
                                <CardFreightPrice>
                                    {getMinPrice(carriers?.dropPoint)}
                                </CardFreightPrice>
                            </Flex>
                            {vendorSelectedDroppoint && (
                                <StyledMessage as="p" variant="bodySmall">
                                    {translate('checkout.vendorSelectedDroppoint')}
                                </StyledMessage>
                            )}
                        </CardWrapper>
                    )}

                    <CardWrapper
                        data-testid="home-delivery"
                        onClick={() => {
                            if (chosenShippingMethod !== DeliveryTypes.addressDelivery) {
                                handleCheckboxChange(DeliveryTypes.addressDelivery);
                            }
                        }}
                        disabled={vendorSelectedDroppoint}
                    >
                        <Flex justifyContent="space-between">
                            <CardInfoWrapper>
                                <CardRadioWrapper>
                                    <Radio
                                        checked={
                                            chosenShippingMethod === DeliveryTypes.addressDelivery
                                        }
                                        aria-label={`Levering, ${translate('checkout.checkout.delivery.option.home')}`}
                                        name="home"
                                        onKeyDown={(e: React.KeyboardEvent) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                if (
                                                    chosenShippingMethod !==
                                                    DeliveryTypes.addressDelivery
                                                ) {
                                                    handleCheckboxChange(
                                                        DeliveryTypes.addressDelivery,
                                                    );
                                                }
                                            }
                                        }}
                                    />
                                </CardRadioWrapper>
                                <CardTitle>
                                    {translate('checkout.checkout.delivery.option.home')}
                                </CardTitle>
                                <CardDeliveryDays>
                                    {getDays(carriers?.addressDelivery)}
                                </CardDeliveryDays>
                                {chosenShippingMethod === DeliveryTypes.addressDelivery &&
                                    isDeliveryAddressEmpty && (
                                        <Button
                                            style={{
                                                marginTop: theme.space['4'],
                                                fontSize: theme.fontSizes['xs'],
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setShowStepDrawer(true);
                                            }}
                                        >
                                            {translate('checkout.checkout.delivery.home')}
                                        </Button>
                                    )}

                                <CardDeliveryAddressInfo isHidden={deliveryType !== 'alternative'}>
                                    <Flex justifyContent="space-between">
                                        <div style={{ paddingBottom: '5px' }}>
                                            {carrier?.addressDelivery?.c_image && (
                                                <Image
                                                    src={carrier?.addressDelivery?.c_image}
                                                    alt={carrier?.addressDelivery?.name ?? ''}
                                                    height={24}
                                                    width={35}
                                                    layout="intrinsic"
                                                    objectFit="contain"
                                                />
                                            )}

                                            <CardTitle>{carrier?.addressDelivery?.name}</CardTitle>
                                        </div>

                                        <CardDeliveryAddressEdit
                                            onClick={() => setShowStepDrawer(true)}
                                            tabIndex={0}
                                            onKeyDown={(e: React.KeyboardEvent) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    setShowStepDrawer(true);
                                                }
                                            }}
                                        >
                                            {translate('checkout.delivery.edit')}
                                        </CardDeliveryAddressEdit>
                                    </Flex>

                                    <div style={{ paddingBottom: '6px' }}>
                                        <CardDeliveryAddressTitle>
                                            {`${deliveryAddress?.firstName} ${deliveryAddress?.lastName}`}
                                        </CardDeliveryAddressTitle>

                                        {deliveryAddress?.address1}
                                    </div>
                                    <div>
                                        {`${deliveryAddress?.postalCode} ${deliveryAddress?.city}`}{' '}
                                    </div>
                                </CardDeliveryAddressInfo>
                            </CardInfoWrapper>
                            <CardFreightPrice>
                                {getMinPrice(carriers?.addressDelivery)}
                            </CardFreightPrice>
                        </Flex>
                    </CardWrapper>
                </Form>
            </div>

            <Drawer
                open={showStepDrawer}
                enableVerticalSlideMobile={true}
                onClose={handleCloseDrawer}
            >
                <DeliveryStepResolver
                    chosenShippingMethod={chosenShippingMethod}
                    handleCloseDrawer={handleCloseDrawer}
                    showStepDrawer={showStepDrawer}
                    onClickAndCollectStoreSelectRequest={handleClickAndCollectStoreSelectRequest}
                />
            </Drawer>
        </>
    );
};
